import { ChangeDetectionStrategy, Component, computed, effect, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoggingService } from '@app/core/services';
import { TenantStateService } from '@app/core/services/tenant-state.service';
import { AppSettingsStore } from '@app/core/state/app-settings-store/app-settings-store';
import { ApiKeyPanelMode, ApiKeyPanelState, ApiKeyStoreService } from '@app/core/state/api-key-store.service';
import { AuthStore } from '@app/core/state/auth.store';
import { ToastServicev2 } from '@app/shared/toast/services';
import { NavigationService } from '@modules/navigation/services';
import { ROUTE_ROOTS } from '@modules/navigation/models';

@Component({
    selector: 'sb-top-nav',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './top-nav.component.html',
    styleUrls: ['top-nav.component.scss'],
})
export class TopNavComponent implements OnInit {
    @Input() rtl = false;
    @Input() sideNavVisilibity = false;

    // Add collapse state for the burger menu
    isNavbarCollapsed = true;
    apiKeyInputValue = '';
    apiKeySubmitted = false;

    readonly apiKeyState$ = this.apiKeyStore.apiKeyState$;
    readonly apiKeyPanelState$ = this.apiKeyStore.panelState$;

    // Computed for client display name with fallbacks
    clientDisplayName = computed(() =>
        this.authStore.userInfoData()?.clientDisplayName ||
        this.tenantStateService.tenantName() ||
        ''
    );

    displayName = computed(() =>
        this.appSettingsStore.settings()?.user?.name ||
        this.authStore.userInfoData()?.firstName ||
        this.authStore.userInfoData()?.username ||
        ''
    );

    constructor(
        private navigationService: NavigationService,
        public appSettingsStore: AppSettingsStore,
        public authStore: AuthStore,
        public tenantStateService: TenantStateService,
        public router: Router,
        private loggingService: LoggingService,
        private readonly apiKeyStore: ApiKeyStoreService,
        private readonly toastService: ToastServicev2
    ) {
        effect(() => {
            const settings = this.appSettingsStore.settings();
            this.loggingService.debug('⚙️ AppSettings updated:', settings);
        });
    }

    ngOnInit() {}
    toggleSideNav() {
        this.navigationService.toggleSideNav(this.sideNavVisilibity);
    }

    toggleNavbar() {
        // Toggle the navbar collapse state
        this.isNavbarCollapsed = !this.isNavbarCollapsed;
    }

    openApiKeyPanel(): void {
        this.resetApiKeyPanelForm();
        this.apiKeyStore.openPanel();
    }

    closeApiKeyPanel(): void {
        this.apiKeyStore.closePanel();
        this.resetApiKeyPanelForm();
    }

    enterApiKeyEditMode(): void {
        this.resetApiKeyPanelForm();
        this.apiKeyStore.openPanel('edit');
    }

    onApiKeyInputChange(value: string): void {
        this.apiKeyInputValue = value;
        if (value.trim()) {
            this.apiKeySubmitted = false;
        }
    }

    saveApiKey(panelState: ApiKeyPanelState): void {
        this.apiKeySubmitted = true;
        const apiKey = this.apiKeyInputValue.trim();

        if (!apiKey) {
            return;
        }

        this.apiKeyStore.setApiKey(apiKey);
        this.apiKeyStore.closePanel();
        this.resetApiKeyPanelForm();

        const message =
            panelState.mode === 'edit'
                ? 'API key updated successfully.'
                : 'API key saved successfully.';
        this.toastService.showSuccess('API Key', message);
    }

    isApiKeyFormMode(mode: ApiKeyPanelMode): boolean {
        return mode === 'setup' || mode === 'edit';
    }

    isSettingsMenuActive(): boolean {
        return this.router.url.startsWith(`/${ROUTE_ROOTS.PLATFORM}/reconciliationsettings`)
            || this.router.url.startsWith(`/${ROUTE_ROOTS.PLATFORM}/consolidationsettings`);
    }

    private resetApiKeyPanelForm(): void {
        this.apiKeyInputValue = '';
        this.apiKeySubmitted = false;
    }
}
